import { useQuery } from "@apollo/client/react";
import { useState } from "react";

import { Stack, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { ImportStatus, ImportStatusDocument } from "../../graphql/types";
import type { DownloadJournal } from "../../graphql/schema-types";
import type { ImportStatusQuery, ImportStatusQueryVariables } from "../../graphql/types";

import { FunnelIcon } from "@phosphor-icons/react/dist/csr/Funnel";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { ImportStatusFilters, createDefaultImportStatusFilters } from "../DownloadFilters";
import type { IImportStatusFilter } from "../DownloadFilters";
import { LinkDownloadsByImport } from "../LinksHelper";
import { StatusChart } from "../StatusChart";
import { buildStats } from "./buildStats";
import { BaseWelcomeMessage } from "../WelcomeMessage";
import { QueryResult } from "../common/QueryResult";
import { DefaultPanel } from "../ui/panels";

const PAGE_SIZE = 1000;
const DEFAULT_LOOKBACK_DAYS = 30;

const ImportStatusList = ({ rows, project = null }: { rows: ImportStatus[]; project?: string | null }) => {
  const filteredImports = rows.filter((importStatus: ImportStatus) => {
    return project === null || project === "" || importStatus.project === project;
  });

  const importStatuses = filteredImports.map((importStatus: ImportStatus, index: number) => {
    const journalByDate = importStatus.journal?.reduce<Record<string, DownloadJournal>>(function (map, obj) {
      if (obj) {
        map[String(obj.date)] = obj;
      }
      return map;
    }, {});
    const keys = Object.keys(journalByDate ?? {});
    if (keys.length === 1 && journalByDate && journalByDate[keys[0]].date === null) {
      return (
        <Stack key={index} direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <WarningIcon color="warning" data-testid="error-icon" />
          <Typography>This import does not have a report date. Please try grouping by the download date.</Typography>
        </Stack>
      );
    }
    const importStatusWithStats = {
      ...importStatus,
      journal: buildStats(DEFAULT_LOOKBACK_DAYS, journalByDate),
    };
    const viewFiles = importStatusWithStats.name ? (
      <LinkDownloadsByImport import_name={importStatusWithStats.name} />
    ) : null;
    return (
      <StatusChart
        key={index}
        data={importStatusWithStats}
        prefix={"Downloads"}
        viewFiles={viewFiles}
        assetType={"Import"}
      />
    );
  });

  return <div>{importStatuses}</div>;
};

const NoFilters = () => (
  <BaseWelcomeMessage icon={FunnelIcon} title={"Select a Project or Import"}>
    <Typography>Select a Project or Import to view download activity.</Typography>
  </BaseWelcomeMessage>
);

const ImportStatusDashboardCharts = ({
  dateRangeType,
  importName: importNameProp,
  project: projectProp,
  first = PAGE_SIZE,
}: IImportStatusFilter & { first?: number }) => {
  const importName = importNameProp ?? "";
  const project = projectProp ?? "";
  const hasValue = importName !== "" || project !== "";

  const variables = {
    first,
    lookback_days: DEFAULT_LOOKBACK_DAYS,
    name: importName,
    project,
    group_by: dateRangeType,
  };

  const { loading, error, data } = useQuery<ImportStatusQuery, ImportStatusQueryVariables>(ImportStatusDocument, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: variables,
  });

  if (hasValue) {
    return (
      <QueryResult
        data={data}
        error={error}
        loading={loading}
        nodeName="import_status"
        emptyElement="No Imports"
        resultsMapper={(rows: Array<ImportStatus>) => <ImportStatusList rows={rows} project={project} />}
      />
    );
  } else {
    return <NoFilters />;
  }
};

const ImportStatusDashboard = () => {
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState(
    createDefaultImportStatusFilters({
      project: searchParams.get("project_id"),
      importName: searchParams.get("import_name"),
    })
  );
  return (
    <DefaultPanel.Container>
      <DefaultPanel.Header>
        <ImportStatusFilters
          {...filter}
          onChange={setFilter}
          onClear={() => setFilter(createDefaultImportStatusFilters({}))}
        />
      </DefaultPanel.Header>
      <DefaultPanel.Body>
        <div className="ImportStatusDashboard" data-cy="import-status-dashboard">
          <ImportStatusDashboardCharts {...filter} />
        </div>
      </DefaultPanel.Body>
    </DefaultPanel.Container>
  );
};

export { ImportStatusDashboard };
